import { ADMIN_ENDPOINTS, FETCH_OPTIONS } from '../../config/constants';

export interface RiepilogoUfficio {
  id_struttura: number;
  uffici: string;
  ore_totali: number;
  giorni_lavorati: number;
  media_ore_giorno: number;
}

export interface RiepilogoOperatore {
  id_operatore: number;
  operatore: string;
  ore_totali: number;
  giorni_lavorati: number;
  numero_uffici: number;
  strutture: string;
}

export interface Stats {
  ore_totali: number;
  uffici_attivi: number;
  operatori: number;
}

export interface FiltriRicerca {
  dataInizio: string;
  dataFine: string;
  strutturaSelezionata?: string;
  operatoreSelezionato?: string;
}

/**
 * Costruisce i parametri della query per le chiamate API
 */
const costruisciParametri = (filtri: FiltriRicerca): URLSearchParams => {
  const params = new URLSearchParams();
  if (filtri.dataInizio) params.append('data_inizio', `${filtri.dataInizio}T00:00:00`);
  if (filtri.dataFine) params.append('data_fine', `${filtri.dataFine}T23:59:59`);
  if (filtri.strutturaSelezionata) params.append('id_struttura', filtri.strutturaSelezionata);
  if (filtri.operatoreSelezionato) params.append('id_operatore', filtri.operatoreSelezionato);
  return params;
};

/**
 * Carica le statistiche generali
 */
export const fetchStats = async (filtri: FiltriRicerca): Promise<Stats> => {
  const params = costruisciParametri(filtri);
  const response = await fetch(`${ADMIN_ENDPOINTS.GET_STATS}?${params.toString()}`, FETCH_OPTIONS);
  
  if (!response.ok) {
    throw new Error('Errore nel caricamento delle statistiche');
  }

  const data = await response.json();
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Carica il riepilogo per uffici
 */
export const fetchRiepilogoUffici = async (filtri: FiltriRicerca): Promise<RiepilogoUfficio[]> => {
  const params = costruisciParametri(filtri);
  const response = await fetch(`${ADMIN_ENDPOINTS.GET_STRUTTURE}?${params.toString()}`, FETCH_OPTIONS);
  
  if (!response.ok) {
    throw new Error('Errore nel caricamento del riepilogo uffici');
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Carica il riepilogo per operatori
 */
export const fetchRiepilogoOperatori = async (filtri: FiltriRicerca): Promise<RiepilogoOperatore[]> => {
  const params = costruisciParametri(filtri);
  const response = await fetch(`${ADMIN_ENDPOINTS.GET_OPERATORI}?${params.toString()}`, FETCH_OPTIONS);
  
  if (!response.ok) {
    throw new Error('Errore nel caricamento del riepilogo operatori');
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Esporta un file Excel (uffici o operatori)
 */
export const esportaExcel = async (
  tipo: 'Uffici' | 'Operatori',
  filtri: FiltriRicerca
): Promise<void> => {
  const endpoint = tipo === 'Uffici' 
    ? ADMIN_ENDPOINTS.EXPORT_EXCEL_UFFICI 
    : ADMIN_ENDPOINTS.EXPORT_EXCEL_OPERATORI;

  const params = new URLSearchParams({
    data_inizio: filtri.dataInizio,
    data_fine: filtri.dataFine,
  });
  
  if (filtri.strutturaSelezionata) {
    params.append('id_struttura', filtri.strutturaSelezionata);
  }
  
  if (filtri.operatoreSelezionato) {
    params.append('id_operatore', filtri.operatoreSelezionato);
  }

  const response = await fetch(`${endpoint}?${params.toString()}`, {
    ...FETCH_OPTIONS,
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Errore durante il download del file Excel');
  }

  // Crea un blob dal contenuto della risposta
  const blob = await response.blob();
  
  // Estrae il nome del file dall'header Content-Disposition
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'download.xlsx'; // Nome di default
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }
  
  // Crea un URL temporaneo per il blob
  const url = window.URL.createObjectURL(blob);
  
  // Crea un elemento <a> nascosto per forzare il download
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  
  // Pulisce
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
